"use client";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  PostHogConfig,
  PostHogProvider,
} from "@/lib/providers/posthog-provider";
import { SentryConfig, SentryProvider } from "@/lib/providers/sentry-provider";
import { ExtendedUser } from "@/server/actions/fetch-or-create-user";
import { TRPCReactProvider } from "../@/trpc/react";
import { AppProvider } from "@/contexts/AppContext";
import type { Plan } from "@stigg/react-sdk";
import { StiggWrapper } from "@/contexts/StiggWrapper";
import { useGlobalErrorHandler } from "@/hooks/useErrorHandler";
import { usePathname } from "next/navigation";

interface Props {
  children: React.ReactNode;
  sentryConfig: SentryConfig;
  posthogConfig: PostHogConfig;
  stiggConfig: {
    apiKey: string;
  };
  extendedUser: ExtendedUser | null;
  currentPlan: Plan | null;
}

export const ClientProviders = ({
  children,
  sentryConfig,
  posthogConfig,
  stiggConfig,
  extendedUser,
  currentPlan,
}: Props) => {
  // Initialize global error handling
  useGlobalErrorHandler();

  const pathname = usePathname();

  // Pages that should not use AppProvider (to avoid tRPC queries that require org membership)
  const skipAppProvider = pathname === "/invitation/accept";

  return (
    <SentryProvider sentryConfig={sentryConfig}>
      <TRPCReactProvider>
        <PostHogProvider postHogConfig={posthogConfig}>
          <TooltipProvider>
            <StiggWrapper
              apiKey={stiggConfig.apiKey}
              customerId={extendedUser?.organization?.id}
              customerToken={extendedUser?.customerToken}
            >
              {skipAppProvider ? (
                children
              ) : (
                <AppProvider
                  context={{
                    user: extendedUser,
                    currentPlan,
                  }}
                >
                  {children}
                </AppProvider>
              )}
            </StiggWrapper>
          </TooltipProvider>
        </PostHogProvider>
      </TRPCReactProvider>
    </SentryProvider>
  );
};
